import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Check, Copy } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useToast } from '../ui/Toast';

/** Syntax-highlighted code with a one-click copy button. */
export default function CodeBlock({ code = '', language = 'javascript', className, maxHeight }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className={cn('overflow-hidden rounded-xl border border-border/70 bg-[#1a1a26]', className)}>
      <div className="flex items-center justify-between border-b border-border/70 bg-white/[0.02] px-4 py-2">
        <span className="font-mono text-xs uppercase tracking-wide text-content-subtle">
          {language}
        </span>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-content-muted transition-colors hover:bg-white/5 hover:text-content"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="overflow-auto" style={maxHeight ? { maxHeight } : undefined}>
        <SyntaxHighlighter
          language={language}
          style={oneDark}
          customStyle={{ margin: 0, background: 'transparent', fontSize: 13, padding: '1rem' }}
          codeTagProps={{ style: { fontFamily: 'var(--font-mono, monospace)' } }}
          wrapLongLines
        >
          {code}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}
